import {ChangeDetectionStrategy, Component, computed, inject} from '@angular/core'
import {DecimalPipe} from '@angular/common'
import {BetslipWorkbenchStore} from './betslip-workbench.store'
import {LiveOddsTicker, TICK_INTERVALS} from './live-odds-ticker'

@Component({
	selector: 'dt-workbench-offer-panel',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [DecimalPipe],
	template: `
		<header class="offer-head">
			<h2>Offer <span class="muted">{{ offerSize() }} selections</span></h2>

			<div class="live-controls" [class.running]="ticker.running()">
				<button type="button" class="live-toggle" (click)="ticker.setRunning(!ticker.running())"
						[title]="ticker.running() ? 'Pause live odds' : 'Resume live odds'">
					<span class="dot"></span>
					{{ ticker.running() ? 'Live odds on' : 'Live odds paused' }}
				</button>
				<select [value]="ticker.intervalMs()" (change)="onInterval($event)" title="How often live odds move">
					@for (ms of intervals; track ms) {
						<option [value]="ms" [selected]="ms === ticker.intervalMs()">every {{ ms / 1000 }}s</option>
					}
				</select>
				<button type="button" class="link" (click)="ticker.tick()">tick now</button>
			</div>
		</header>
		<p class="hint">
			betslip-demo's fixed offer. Odds on <span class="live">LIVE</span> matches move by a few percent every
			tick and reach the betslip through production's <code>updateSelections</code>, like the live feed.
			Pick two selections on the same match to turn the Betslip into a Bet Builder.
			<span class="muted">{{ ticker.tickCount() }} ticks so far.</span>
		</p>

		@for (match of ticker.offerByMatch(); track match[0].matchId) {
			<section class="match" [class.is-live]="match[0].isLiveMatch">
				<header>
					<span class="teams">{{ match[0].homeName }} <em>v</em> {{ match[0].awayName }}</span>
					<span class="meta">
						{{ match[0].league }}
						@if (match[0].isLiveMatch) {
							<span class="live"><span class="dot"></span>LIVE</span>
						}
					</span>
				</header>

				<div class="outcomes">
					@for (selection of match; track selection.oddsId) {
						@let move = ticker.moves().get(selection.oddsId);
						<button type="button"
								[class.picked]="isPicked(selection.oddsId)"
								[class.up]="move?.direction === 'up'"
								[class.down]="move?.direction === 'down'"
								(click)="store.toggle(selection)">
							<span class="market">{{ selection.marketLabel }}</span>
							<span class="outcome">{{ selection.outcomeLabel }}</span>
							<!-- Fixed-size price column: the old price keeps its line whether or not there is a move,
								 so a tick never changes the box's height or shifts the odds sideways. -->
							<span class="price">
								<span class="odds">
									<span class="arrow">{{ move ? (move.direction === 'up' ? '▲' : '▼') : '' }}</span>
									{{ selection.odds | number: '1.2-2' }}
								</span>
								<span class="was" [class.empty]="!move">was {{ move?.from ?? 0 | number: '1.2-2' }}</span>
							</span>
						</button>
					}
				</div>
			</section>
		}
	`
})
export class WorkbenchOfferPanelComponent {
	readonly store = inject(BetslipWorkbenchStore)
	readonly ticker = inject(LiveOddsTicker)
	readonly intervals = TICK_INTERVALS
	readonly offerSize = computed(() => this.ticker.offer().length)

	isPicked(oddsId: number): boolean {
		return this.store.selections().some((selection) => selection.oddsId === oddsId)
	}

	onInterval(event: Event): void {
		this.ticker.setInterval(Number((event.target as HTMLSelectElement).value))
	}
}
